import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { WebPageSchema, BreadcrumbListSchema } from '@/components/feature/SeoSchema';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'announcements');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const category = getCategoryBySlug(activeSlug);
  const articles = getArticlesByCategory(activeSlug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <WebPageSchema
        title={`${category.name} - 琉璃神社官方`}
        description={category.description}
        url={`https://liulishenshe.com/category/${activeSlug}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://liulishenshe.com' },
          { name: category.name, url: `https://liulishenshe.com/category/${activeSlug}` },
        ]}
      />

      <Navbar />

      <main>
        <div className="relative h-48 md:h-64 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${category.coverImage}')` }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"></div>
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm mb-3">
              <i className={`${category.icon} text-xl text-white`}></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
              {category.name}
            </h1>
            <p className="text-white/70 text-sm max-w-md text-center">
              {category.description}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} - 琉璃神社官方`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {article.isPinned && (
                      <span className="absolute top-3 left-3 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-md font-medium">
                        置顶
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full font-medium">
                        {article.tags[0]}
                      </span>
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors text-sm leading-snug line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无内容</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}